/**
 * interior.ts — which buildings are ENTERABLE, and what template class their
 * interior follows (P1 of the 2026-07-28 building-interiors design,
 * docs/superpowers/specs/2026-07-28-building-interiors-design.md).
 *
 * The building TYPE axis is `Parcel.use` (worldgen/citygraph.ts USE_* codes),
 * carried on the building record as field 15 since the P0 stride-17 extension.
 * This module maps use → interior template class; a building whose use maps to
 * a class is generated HOLLOW (buildingPlan `hollow: true` → shell walls with
 * a carved doorway instead of solid module blocks). Uses absent here keep the
 * solid fold — SANCTUM/STORAGE/PLAIN classes are the design doc's second wave.
 *
 * SHARED-CODE DISCIPLINE: both the client bake (buildingMeshes.ts) and the
 * collision fold (settlementCollision.ts, client AND server) derive hollowness
 * through {@link interiorClassForUse} — interiors that exist only on one side
 * are the bolt-through-wall bug class (cache/walls.ts header).
 */
import {
  USE_DWELLING, USE_HEADMAN,
  USE_INN,
  USE_MARKET_HALL, USE_SMITHY, USE_WORKSHOP, USE_APOTHECARY, USE_DEPOT,
  USE_HALL, USE_ADMIN, USE_COURTHOUSE, USE_GUILDHALL,
  USE_GARRISON, USE_BUNKHOUSE, USE_PRISON, USE_WATCHTOWER,
  USE_PRODUCTION, USE_MILL, USE_WAREHOUSE, USE_DOCKYARD, USE_FUEL_SHED,
  USE_STABLE, USE_STARPORT,
  USE_TEMPLE, USE_CHAPEL, USE_BASILICA, USE_SHRINE, USE_ACADEMY, USE_THEATER,
  USE_PALACE, USE_EMBASSY, USE_BATHHOUSE,
  USE_HARBOR, USE_ARENA, USE_LOOM_NEXUS,
} from "../worldgen/citygraph.js";

/** Interior template classes. Wave 1: home/tavern/shop/civic (§D3). Wave 2
 *  (2026-07-31 "every building has an interior"): barracks/works/sanctum. */
export type InteriorClass = "home" | "tavern" | "shop" | "civic"
  | "barracks" | "works" | "sanctum";

const USE_INTERIOR: ReadonlyMap<number, InteriorClass> = new Map<number, InteriorClass>([
  [USE_DWELLING, "home"], [USE_HEADMAN, "home"],
  [USE_INN, "tavern"], [USE_BATHHOUSE, "tavern"],
  [USE_MARKET_HALL, "shop"], [USE_SMITHY, "shop"], [USE_WORKSHOP, "shop"],
  [USE_APOTHECARY, "shop"], [USE_DEPOT, "shop"],
  [USE_HALL, "civic"], [USE_ADMIN, "civic"], [USE_COURTHOUSE, "civic"],
  [USE_GUILDHALL, "civic"], [USE_PALACE, "civic"], [USE_EMBASSY, "civic"],
  // Wave 2 (2026-07-31): soldiers, industry and faith get walk-in workplaces.
  [USE_GARRISON, "barracks"], [USE_BUNKHOUSE, "barracks"], [USE_PRISON, "barracks"],
  [USE_WATCHTOWER, "barracks"],
  [USE_PRODUCTION, "works"], [USE_MILL, "works"], [USE_WAREHOUSE, "works"],
  [USE_DOCKYARD, "works"], [USE_FUEL_SHED, "works"], [USE_STABLE, "works"],
  [USE_STARPORT, "works"],
  [USE_TEMPLE, "sanctum"], [USE_CHAPEL, "sanctum"], [USE_BASILICA, "sanctum"],
  [USE_SHRINE, "sanctum"], [USE_ACADEMY, "sanctum"], [USE_THEATER, "sanctum"],
  // 2026-08-02 unauthored-uses audit: harbours are dock-works, arenas read as
  // a fight school (racks + benches), and the Loom nexus opens as the grandest
  // sanctum. USE_PRECURSOR stays SEALED on purpose (lore); USE_LANDING_PAD is
  // an OPEN parcel now (authored platform, no building).
  [USE_HARBOR, "works"], [USE_ARENA, "barracks"], [USE_LOOM_NEXUS, "sanctum"],
]);

/** The interior template class for a parcel use, or null (building stays solid). */
export function interiorClassForUse(use: number): InteriorClass | null {
  return USE_INTERIOR.get(use) ?? null;
}

/** True iff a building with this parcel use generates a walk-in shell. */
export function interiorEnabledUse(use: number): boolean {
  return USE_INTERIOR.has(use);
}

/** Steepest footprint (corner ground spread, record field 17) that still gets
 *  an interior: the floor slab rises to cover the spread, and past ~1 m the
 *  remaining headroom under the 4 m storey stops being a room. Steeper
 *  buildings simply stay solid. */
export const INTERIOR_MAX_SPREAD_M = 1.0;

/** THE hollow predicate — use + terrain together (record fields 15 + 17).
 *  Every consumer (collision fold, client bake, oracles, dev rigs) derives
 *  hollowness through this one function; a disagreement is the
 *  bolt-through-wall bug class. */
export function interiorEligible(use: number, groundSpreadM: number): boolean {
  return USE_INTERIOR.has(use) && groundSpreadM <= INTERIOR_MAX_SPREAD_M;
}

// ═══ interiorPlan — rooms, furniture, anchors (P2) ════════════════════════════
//
// A pure, deterministic layer OVER a hollow BuildingPlan: given the plan (its
// shell + door), the parcel use and the buildingSeed already inside the plan,
// produce partition walls (with an interior doorway), furniture placements,
// named NPC anchors (bed / seat / counter / hearth — P4's join surface) and
// the furniture/partition COLLISION boxes. plan.ts stays untouched; the two
// consumers (settlementCollision fold + buildingMeshes bake) call this beside
// buildingPlan, so client and server stay byte-identical by construction.
//
// Randomness: layerRng(seed, LAYER_ROOMS / LAYER_FURNISH) — appended layers on
// the plan's own 0x51ab fan-out; no existing stream shifts.
//
// SHARED-CODE DISCIPLINE: pmath-ban applies — integer/quarter-turn math only.

import {
  layerRng, LAYER_ROOMS, LAYER_FURNISH, SIDE_ROT, ROT_DX, ROT_DZ,
  SHELL_WALL_T, SHELL_DOOR_W, SHELL_DOOR_HEAD_M,
  type BuildingPlan, type LocalBox, type ShellWall,
} from "./plan.js";
import { MODULE_UNIT_M, LEVEL_H_M } from "./kit.js";

/** One placed furniture piece (kit `int-*` id; also `grb-crate` is reused).
 *  (x, z) building-local metres ON the storey's floor — level 0 sits at
 *  shell.floorTopM, level L at L·H (the bake/fold add the height);
 *  rot = quarter-turns, local +z = facing. */
export interface FurniturePlacement { piece: string; x: number; z: number; rot: 0 | 1 | 2 | 3; level: number }

/** Named NPC/light anchor. `rot` = quarter-turn the occupant faces. */
export interface InteriorAnchor {
  kind: "bed" | "seat" | "counter" | "hearth" | "lamp";
  x: number; z: number; rot: 0 | 1 | 2 | 3; level: number;
}

export interface InteriorPlan {
  cls: InteriorClass;
  /** Room count on the ground floor (1 + successful partition cuts). */
  rooms: number;
  /** Partition walls on interior cell edges (same shape as shell walls; the
   *  bake renders them with the shell-wall / shell-wall-door pieces). */
  partitions: ShellWall[];
  furniture: FurniturePlacement[];
  anchors: InteriorAnchor[];
  /** Furniture + partition collision, building-local, BASE-relative (the same
   *  frame as plan.collision — fold with no extra offset): partitions span
   *  the full storey from y = 0 exactly like shell walls (`wall: true`);
   *  furniture boxes already sit at y = shell.floorTopM. */
  collision: LocalBox[];
  /** shell.floorTopM, echoed for the bake (furniture pieces emit at this y;
   *  partition pieces emit at y = 0, base-relative like shell walls). */
  floorTopM: number;
}

/** Per-piece collision half-extent table (w, h, d full extents at rot 0).
 *  null = no collision (rug). Kept here — the kit docs are visual-only. */
const PIECE_BOX: ReadonlyMap<string, readonly [number, number, number] | null> = new Map([
  ["int-bed", [2.0, 0.6, 1.1]],
  ["int-table", [1.6, 0.92, 1.0]],
  ["int-stool", [0.45, 0.56, 0.45]],
  ["int-bench", [1.5, 0.56, 0.45]],
  ["int-counter", [2.56, 1.05, 0.82]],
  ["int-shelf", [1.8, 2.1, 0.5]],
  ["int-cabinet", [1.3, 1.75, 0.6]],
  ["int-hearth", [1.6, 1.8, 0.85]],
  ["int-rug", null],
  ["int-lamp", [0.34, 1.95, 0.34]],
  ["int-barrel", [0.9, 0.95, 0.9]],
  ["int-lectern", [0.55, 1.25, 0.55]],
  ["grb-crate", [1.6, 0.85, 1.5]],
  ["int-chest", [0.95, 0.6, 0.56]],
  ["int-nightstand", [0.55, 0.7, 0.5]],
  ["int-stove", [0.95, 0.9, 0.65]],
  ["int-basin", [0.65, 0.9, 0.55]],
  ["int-plant", [0.55, 1.2, 0.55]],
  ["int-bookshelf", [1.7, 2.05, 0.45]],
  // P-B4 workplace pieces (2026-07-31).
  ["int-forge", [1.5, 2.1, 0.95]],
  ["int-anvil", [0.9, 0.75, 0.55]],
  ["int-workbench", [1.85, 1.1, 0.8]],
  ["int-stall", [2.2, 2.0, 0.95]],
  ["int-keg", [1.45, 1.5, 0.85]],
  ["int-desk", [1.55, 0.9, 0.85]],
  ["int-cauldron", [0.95, 0.95, 0.95]],
  // Interiors wave 2 (2026-07-31).
  ["int-rack", [1.75, 1.95, 0.4]],
  ["int-machine", [1.95, 2.1, 1.05]],
  ["int-altar", [1.95, 1.8, 0.95]],
]);

/** Placement footprint (w, d) for pieces with no collision (rug) — used only
 *  by the overlap-rejection spacing, never folded. */
const PLACE_BOX: ReadonlyMap<string, readonly [number, number]> = new Map([
  ["int-rug", [2.6, 1.9]],
]);

const SIDE_DX = [0, 0, 1, -1] as const;
const SIDE_DZ = [1, -1, 0, 0] as const;
/** Opposite SIDE index (0↔1, 2↔3). A piece against side s faces the room —
 *  i.e. carries its local +z toward OPP[s] — so its rot = SIDE_ROT[OPP[s]]. */
const OPP = [1, 0, 3, 2] as const;
// ROT_DX/ROT_DZ live in plan.ts (the stair placement needs them there);
// re-exported so existing consumers keep resolving.
export { ROT_DX, ROT_DZ };

interface Slot {
  x: number; z: number; rot: 0 | 1 | 2 | 3; gx: number; gz: number; taken: boolean;
  /** Wall slots only: which cell edge the slot hugs, and that wall's kind
   *  (art hangs on `solid` walls only — never across windows/doorways). */
  side?: 0 | 1 | 2 | 3; kind?: ShellWall["kind"];
}

/**
 * Furnish a hollow plan. Returns null for solid plans (no shell) or uses
 * without an interior class. See the module header for the contract.
 */
export function interiorPlan(plan: BuildingPlan, use: number, options: { reserveDoorClearance?: boolean; reserveCellRoutes?: boolean } = {}): InteriorPlan | null {
  const cls = interiorClassForUse(use);
  const sh = plan.shell;
  if (cls === null || sh === null || sh.floors.length === 0) return null;
  const U = MODULE_UNIT_M, T = SHELL_WALL_T;
  const rngRooms = layerRng(plan.seed, LAYER_ROOMS);
  const rng = layerRng(plan.seed, LAYER_FURNISH);

  // Cell bookkeeping in GRID coords (metres → grid via the plan's centring).
  const gxOf = (x: number): number => Math.round(x / U + (plan.cellsW - 1) / 2);
  const gzOf = (z: number): number => Math.round(z / U + (plan.cellsD - 1) / 2);
  const cellKey = (gx: number, gz: number): number => gz * plan.cellsW + gx;
  const floorCells = new Set(sh.floors.map((f) => cellKey(gxOf(f.x), gzOf(f.z))));

  // The entry door cell + the cell just inside it stay CLEAR of furniture —
  // the walk-in path (and P4's NPC threshold path) must never be blocked.
  const doorWall = sh.walls.find((w) => w.kind === "door");
  if (doorWall === undefined) return null;
  const dgx = gxOf(doorWall.x), dgz = gzOf(doorWall.z);
  const inGx = dgx - SIDE_DX[doorWall.side], inGz = dgz - SIDE_DZ[doorWall.side];
  const clearCells = new Set([cellKey(dgx, dgz)]);
  if (floorCells.has(cellKey(inGx, inGz))) clearCells.add(cellKey(inGx, inGz));
  // P3: stair cells are structural — no furniture in the climb volume (a
  // wall-slot hearth inside the stair column was the §11 tread-probe bug).
  for (const st of sh.stairs) clearCells.add(cellKey(gxOf(st.x), gzOf(st.z)));

  // ── rooms (level 0): generalized zone cuts ──────────────────────────────────
  // HOME: cut 1 = bedroom (the half without the entry door); cut 2 = a galley
  // kitchen sliced off the living half when it is big enough. SHOP/TAVERN: a
  // 1-cell-deep BACKROOM sliced off the far end. Every partition run carries
  // exactly one interior doorway. CIVIC stays one hall.
  const partitions: ShellWall[] = [];
  const zone = new Map<number, "living" | "bedroom" | "kitchen" | "back">();
  for (const k of floorCells) zone.set(k, "living");
  const stairCells0 = new Set(sh.stairs.filter((s) => s.level === 0)
    .map((s) => cellKey(gxOf(s.x), gzOf(s.z))));
  // Stair cell + its bottom-approach cell: no partition may touch either
  // (2026-07-30 "stairways need a gap between their ends and the walls") —
  // a cut line along that boundary put a wall 0.4m from the first tread.
  const stairAdj0 = new Set<number>();
  for (const s of sh.stairs) {
    if (s.level !== 0) continue;
    const sgx = gxOf(s.x), sgz = gzOf(s.z);
    stairAdj0.add(cellKey(sgx, sgz));
    stairAdj0.add(cellKey(sgx - ROT_DX[s.rot], sgz - ROT_DZ[s.rot]));
  }
  /** Slice `depth` columns off one end of `from`-zoned cells along an axis;
   *  emit partition walls (one doorway) between slice and rest; re-zone the
   *  slice. Aborts (returns false) when either side would be empty, or the
   *  slice would swallow the entry/stair cells. */
  const carve = (from: "living", to: "bedroom" | "kitchen" | "back",
    alongX: boolean, takeHigh: boolean, depth: number): boolean => {
    const cells = [...floorCells].filter((k) => zone.get(k) === from);
    if (cells.length < 2) return false;
    let lo = Infinity, hi = -Infinity;
    for (const k of cells) {
      const c = alongX ? k % plan.cellsW : Math.floor(k / plan.cellsW);
      if (c < lo) lo = c; if (c > hi) hi = c;
    }
    if (hi - lo + 1 < depth + 1) return false; // both sides must survive
    const inSlice = (k: number): boolean => {
      const c = alongX ? k % plan.cellsW : Math.floor(k / plan.cellsW);
      return takeHigh ? c > hi - depth : c < lo + depth;
    };
    const slice = cells.filter(inSlice);
    if (slice.length === 0 || slice.length === cells.length) return false;
    for (const k of slice) {
      if (clearCells.has(k) || stairCells0.has(k)) return false; // entry/stairs stay in the hall
    }
    // Partition faces: slice cells' boundary toward the rest.
    const side: 0 | 1 | 2 | 3 = alongX ? (takeHigh ? 3 : 2) : (takeHigh ? 1 : 0);
    const runs: { x: number; z: number }[] = [];
    for (const k of slice) {
      const gx = k % plan.cellsW, gz = Math.floor(k / plan.cellsW);
      const ngx = gx + SIDE_DX[side], ngz = gz + SIDE_DZ[side];
      if (ngx < 0 || ngx >= plan.cellsW || ngz < 0 || ngz >= plan.cellsD) continue;
      const nk = cellKey(ngx, ngz);
      if (!floorCells.has(nk) || inSlice(nk) || zone.get(nk) !== from) continue;
      if (stairAdj0.has(k) || stairAdj0.has(nk)) continue; // clear of stair ends
      runs.push({ x: (gx - (plan.cellsW - 1) / 2) * U, z: (gz - (plan.cellsD - 1) / 2) * U });
    }
    if (runs.length === 0) return false;
    const doorIdx = Math.floor(rngRooms() * runs.length);
    const doors = new Set([doorIdx]);
    if (options.reserveCellRoutes) {
      // A courtyard can split one partition cut into separate wings. Each
      // connected run needs its own opening; one door for the whole cut can
      // otherwise seal an entire wing behind an apparently valid floor plan.
      const unseen = new Set(runs.map((_, i) => i));
      while (unseen.size) {
        const group = [unseen.values().next().value!]; unseen.delete(group[0]);
        for (let head=0;head<group.length;head++) for (const i of unseen) {
          const a=runs[group[head]],b=runs[i];
          if(Math.abs(a.x-b.x)+Math.abs(a.z-b.z)===U){group.push(i);unseen.delete(i);}
        }
        if (!group.some(i=>doors.has(i))) doors.add(group[0]);
      }
    }
    runs.forEach((r, i) => partitions.push({
      x: r.x, z: r.z, side, kind: doors.has(i) ? "door" : "solid",
    }));
    for (const k of slice) zone.set(k, to);
    return true;
  };
  let gx0 = Infinity, gx1 = -Infinity, gz0 = Infinity, gz1 = -Infinity;
  for (const f of sh.floors) {
    const gx = gxOf(f.x), gz = gzOf(f.z);
    if (gx < gx0) gx0 = gx; if (gx > gx1) gx1 = gx;
    if (gz < gz0) gz0 = gz; if (gz > gz1) gz1 = gz;
  }
  const spanX = gx1 - gx0 + 1, spanZ = gz1 - gz0 + 1;
  let rooms = 1;
  if (cls === "home" && sh.floors.length >= 4 && Math.min(spanX, spanZ) >= 2) {
    // Bedroom off the end AWAY from the door along the longer axis.
    const alongX = spanX >= spanZ;
    const doorC = alongX ? dgx - gx0 : dgz - gz0;
    const span = alongX ? spanX : spanZ;
    const bedroomDepth = Math.max(1, Math.floor(span / 2) - (span > 3 ? 1 : 0));
    if (carve("living", "bedroom", alongX, doorC < span / 2, bedroomDepth)) rooms++;
    // Galley kitchen off the remaining living space, across the OTHER axis.
    const livingLeft = [...floorCells].filter((k) => zone.get(k) === "living").length;
    if (livingLeft >= 5 && (alongX ? spanZ : spanX) >= 3) {
      if (carve("living", "kitchen", !alongX, rngRooms() < 0.5, 1)) rooms++;
    }
  } else if ((cls === "shop" || cls === "tavern") && sh.floors.length >= 6) {
    // Backroom: 1-deep slice off the end away from the door, longer axis.
    const alongX = spanX >= spanZ;
    const doorC = alongX ? dgx - gx0 : dgz - gz0;
    const span = alongX ? spanX : spanZ;
    if (span >= 3 && carve("living", "back", alongX, doorC < span / 2, 1)) rooms++;
  }

  // ── slots per LEVEL: wall-anchored + cell-centre, structural cells excluded ─
  const shuffle = <X,>(arr: X[]): X[] => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  };
  const nLevels = sh.multiFloor ? plan.levels : 1;
  const wallSlotsL: Slot[][] = [];
  const centreSlotsL: Slot[][] = [];
  for (let L = 0; L < nLevels; L++) {
    // Structural keep-clear at this level: entry cells (L0), stair base cells
    // (climb volume) and the stairwell opening cell above each stair.
    const clearL = new Set<number>();
    if (L === 0) for (const k of clearCells) clearL.add(k);
    for (const st of sh.stairs) {
      if (st.level === L || st.level + 1 === L) clearL.add(cellKey(gxOf(st.x), gzOf(st.z)));
    }
    const cells = L === 0 ? sh.floors
      : sh.upperFloors.filter((f) => f.level === L);
    const wallsL: ShellWall[] = L === 0 ? [...sh.walls, ...partitions]
      : sh.upperWalls.filter((w) => w.level === L);
    const ws: Slot[] = [], cs: Slot[] = [];
    for (const f of cells) {
      const gx = gxOf(f.x), gz = gzOf(f.z);
      if (clearL.has(cellKey(gx, gz))) continue;
      cs.push({ x: f.x, z: f.z, rot: (Math.floor(rng() * 4)) as 0 | 1 | 2 | 3, gx, gz, taken: false });
    }
    for (const w of wallsL) {
      if (w.kind === "door") continue; // never against a doorway
      const gx = gxOf(w.x), gz = gzOf(w.z);
      if (clearL.has(cellKey(gx, gz))) continue;
      ws.push({
        x: w.x + SIDE_DX[w.side] * (U / 2 - T - 0.55),
        z: w.z + SIDE_DZ[w.side] * (U / 2 - T - 0.55),
        rot: SIDE_ROT[OPP[w.side]], // face into the room
        gx, gz, taken: false, side: w.side, kind: w.kind,
      });
    }
    // STARVATION RELIEF (2026-07-31, "counters are still not there"): a tiny
    // building whose whole ground floor IS the entry keep-clear (1-cell shops
    // and taverns, 2-cell civic halls — the door cell plus the cell inside it
    // covers every floor cell) ended up with ZERO level-0 slots, so nothing
    // downstairs was furnished at all and the required-anchor backstop had to
    // fall through to an upper storey (or fail). When a level has no slot at
    // all, re-admit its WALL slots only — the doorway wall is already excluded
    // by the `kind === "door"` skip above, so the walk-in path stays open while
    // the remaining three faces become furnishable. Centre slots stay barred:
    // the middle of a one-cell room is the threshold path.
    if (ws.length === 0 && cs.length === 0) {
      for (const w of wallsL) {
        if (w.kind === "door") continue;
        ws.push({
          x: w.x + SIDE_DX[w.side] * (U / 2 - T - 0.55),
          z: w.z + SIDE_DZ[w.side] * (U / 2 - T - 0.55),
          rot: SIDE_ROT[OPP[w.side]],
          gx: gxOf(w.x), gz: gzOf(w.z), taken: false, side: w.side, kind: w.kind,
        });
      }
    }
    wallSlotsL.push(shuffle(ws));
    centreSlotsL.push(shuffle(cs));
  }
  const zoneOf = (s: Slot): string => zone.get(cellKey(s.gx, s.gz)) ?? "living";
  const inBedroom = (s: Slot): boolean => zoneOf(s) === "bedroom";

  // ── overlap-rejecting placement (2026-07-30: "furniture must not clip") ────
  // Every placed piece registers a rotated footprint AABB (+ a small margin);
  // put* helpers scan candidate slots and take the FIRST whose piece fits.
  const placedBoxes: { x0: number; x1: number; z0: number; z1: number; L: number }[] = [];
  if (options.reserveCellRoutes) for (const a of sh.floors) for (const b of sh.floors) {
    if (Math.abs(a.x-b.x)+Math.abs(a.z-b.z)!==U) continue;
    // A narrow circulation spine through connected modules prevents a small
    // wing becoming an inaccessible furniture alcove. Door reservations below
    // also cover offset partition openings.
    placedBoxes.push({x0:Math.min(a.x,b.x)-.5,x1:Math.max(a.x,b.x)+.5,
      z0:Math.min(a.z,b.z)-.5,z1:Math.max(a.z,b.z)+.5,L:0});
  }
  // Program generator 2: reserve BOTH sides of real openings before choosing
  // furniture slots. Version 1 keeps its published arrangement for old worlds.
  if (options.reserveDoorClearance) for (const w of [...sh.walls, ...partitions]) {
    if (w.kind !== 'door') continue;
    const dx=SIDE_DX[w.side],dz=SIDE_DZ[w.side];
    const x=w.x+dx*(U/2-T/2),z=w.z+dz*(U/2-T/2);
    const hx=Math.abs(dx)*1.8+Math.abs(dz)*.65,hz=Math.abs(dz)*1.8+Math.abs(dx)*.65;
    placedBoxes.push({x0:x-hx,x1:x+hx,z0:z-hz,z1:z+hz,L:0});
  }
  // Phantom CORRIDOR boxes at BOTH stair ends (2026-07-30 "invisible barrier
  // at the top of stairs" + "gap at the bottom of the stairs"): the strip past
  // the top mouth (landing, level+1) and the strip behind the bottom tread
  // (approach, level) are seeded as occupied so no furniture can block either
  // end — while the rest of each cell stays furnishable (a blanket cell
  // keep-clear starved small upper storeys of their bed slot).
  const seedCorridor = (mx: number, mz: number, dx: number, dz: number, L: number): void => {
    const half = 1.3, reach = 2.4; // corridor: 2.6 wide, 2.4m past the mouth
    const x0 = Math.min(mx - Math.abs(dz) * half, mx + dx * reach - Math.abs(dz) * half);
    const x1 = Math.max(mx + Math.abs(dz) * half, mx + dx * reach + Math.abs(dz) * half);
    const z0 = Math.min(mz - Math.abs(dx) * half, mz + dz * reach - Math.abs(dx) * half);
    const z1 = Math.max(mz + Math.abs(dx) * half, mz + dz * reach + Math.abs(dx) * half);
    placedBoxes.push({ x0, x1, z0, z1, L });
  };
  for (const st of sh.stairs) {
    const dx = ROT_DX[st.rot], dz = ROT_DZ[st.rot];
    seedCorridor(st.x + dx * (U / 2), st.z + dz * (U / 2), dx, dz, st.level + 1);     // top: into the landing
    seedCorridor(st.x - dx * (U / 2 - 0.4), st.z - dz * (U / 2 - 0.4), -dx, -dz, st.level); // bottom: from tread 1 back through the approach
  }
  const extentOf = (piece: string, rot: number): readonly [number, number] => {
    const pb = PIECE_BOX.get(piece);
    const [w, , d] = pb ?? [0, 0, 0];
    const [pw, pd] = pb ? [w, d] : PLACE_BOX.get(piece) ?? [0.6, 0.6];
    return rot % 2 === 0 ? [pw, pd] : [pd, pw];
  };
  const fitsAt = (piece: string, x: number, z: number, rot: number, L: number): boolean => {
    const [w, d] = extentOf(piece, rot);
    const m = 0.08; // breathing room between pieces
    const x0 = x - w / 2 - m, x1 = x + w / 2 + m, z0 = z - d / 2 - m, z1 = z + d / 2 + m;
    for (const p of placedBoxes) {
      if (p.L !== L) continue;
      if (x0 < p.x1 && x1 > p.x0 && z0 < p.z1 && z1 > p.z0) return false;
    }
    return true;
  };
  /** Total area (m²) this piece would overlap already-placed boxes at a slot —
   *  0 means it fits. The required-anchor backstop's least-bad tie-break. */
  const overlapAt = (piece: string, x: number, z: number, rot: number, L: number): number => {
    const [w, d] = extentOf(piece, rot);
    const x0 = x - w / 2, x1 = x + w / 2, z0 = z - d / 2, z1 = z + d / 2;
    let area = 0;
    for (const p of placedBoxes) {
      if (p.L !== L) continue;
      const ox = Math.min(x1, p.x1) - Math.max(x0, p.x0);
      const oz = Math.min(z1, p.z1) - Math.max(z0, p.z0);
      if (ox > 0 && oz > 0) area += ox * oz;
    }
    return area;
  };
  const registerAt = (piece: string, x: number, z: number, rot: number, L: number): void => {
    const [w, d] = extentOf(piece, rot);
    placedBoxes.push({ x0: x - w / 2, x1: x + w / 2, z0: z - d / 2, z1: z + d / 2, L });
  };

  // ── furnish per template class, per level ───────────────────────────────────
  const furniture: FurniturePlacement[] = [];
  const anchors: InteriorAnchor[] = [];
  const emitPiece = (piece: string, x: number, z: number, rot: 0 | 1 | 2 | 3, L: number,
    anchor?: InteriorAnchor["kind"]): void => {
    furniture.push({ piece, x, z, rot, level: L });
    registerAt(piece, x, z, rot, L);
    if (anchor !== undefined) {
      if (anchor === "counter") {
        // The anchor is where the BODY stands (2026-08-02 "NPCs clipped into
        // counters"): in FRONT of the piece along its facing, turned back to
        // face it — the lean lands ON the counter edge, not inside the mesh.
        const d = extentOf(piece, rot)[1];
        anchors.push({
          kind: anchor,
          x: x + ROT_DX[rot] * (d / 2 + 0.4),
          z: z + ROT_DZ[rot] * (d / 2 + 0.4),
          rot: ((rot + 2) % 4) as 0 | 1 | 2 | 3, level: L,
        });
      } else {
        anchors.push({ kind: anchor, x, z, rot, level: L });
        if (anchor === 'bed') {
          // Keep the standing approach available after the bed is furnished.
          // A later lamp at the cell centre otherwise blocks the resident.
          const ax = x + ROT_DX[rot] * 0.9, az = z + ROT_DZ[rot] * 0.9;
          placedBoxes.push({x0:ax-.4,x1:ax+.4,z0:az-.4,z1:az+.4,L});
        }
      }
    }
  };
  /** STORE COUNTER (2026-08-02 user call): a free-standing counter between the
   *  door and the room, long axis ACROSS the walk-in approach; the keeper's
   *  anchor sits on the FAR side (away from the door) facing the door — the
   *  merchant meets whoever walks in. Null on tiny rooms (callers fall back to
   *  the wall/centre ladder). */
  const putServiceCounter = (): boolean => {
    const dxIn = -SIDE_DX[doorWall.side], dzIn = -SIDE_DZ[doorWall.side];
    const rot = (doorWall.side <= 1 ? 0 : 1) as 0 | 1; // long axis across the approach
    let faceRot: 0 | 1 | 2 | 3 = 0; // merchant faces the DOOR
    for (const r of [0, 1, 2, 3] as const) {
      if (ROT_DX[r] === SIDE_DX[doorWall.side] && ROT_DZ[r] === SIDE_DZ[doorWall.side]) faceRot = r;
    }
    const depth = PIECE_BOX.get("int-counter")![2];
    for (const k of [2, 1]) {
      const gx = inGx + dxIn * k, gz = inGz + dzIn * k;
      const key = cellKey(gx, gz);
      if (!floorCells.has(key) || clearCells.has(key) || stairCells0.has(key)) continue;
      if ((zone.get(key) ?? "living") !== "living") continue;
      // Bias toward the door inside the cell so the keeper's side has room.
      const x = (gx - (plan.cellsW - 1) / 2) * U - dxIn * 0.7;
      const z = (gz - (plan.cellsD - 1) / 2) * U - dzIn * 0.7;
      if (!fitsAt("int-counter", x, z, rot, 0)) continue;
      furniture.push({ piece: "int-counter", x, z, rot, level: 0 });
      registerAt("int-counter", x, z, rot, 0);
      anchors.push({
        kind: "counter",
        x: x + dxIn * (depth / 2 + 0.42), z: z + dzIn * (depth / 2 + 0.42),
        rot: faceRot, level: 0,
      });
      return true;
    }
    return false;
  };
  /** Take the first free slot whose piece FITS (no clip) and place there. */
  const putWall = (piece: string, L: number, pred?: (s: Slot) => boolean,
    anchor?: InteriorAnchor["kind"]): Slot | null => {
    for (const s of wallSlotsL[L] ?? []) {
      if (s.taken || (pred !== undefined && !pred(s))) continue;
      if (!fitsAt(piece, s.x, s.z, s.rot, L)) continue;
      s.taken = true;
      emitPiece(piece, s.x, s.z, s.rot, L, anchor);
      return s;
    }
    return null;
  };
  const putCentre = (piece: string, L: number, pred?: (s: Slot) => boolean,
    anchor?: InteriorAnchor["kind"]): Slot | null => {
    for (const s of centreSlotsL[L] ?? []) {
      if (s.taken || (pred !== undefined && !pred(s))) continue;
      if (!fitsAt(piece, s.x, s.z, s.rot, L)) continue;
      s.taken = true;
      emitPiece(piece, s.x, s.z, s.rot, L, anchor);
      return s;
    }
    return null;
  };
  /** A table with stools on ±x, seat anchors on the stools; a stool that
   *  would clip a neighbour is simply skipped. Optional rug beneath. */
  const tableSet = (L: number, seats: number, pred?: (s: Slot) => boolean, rug = false): Slot | null => {
    const s = putCentre("int-table", L, pred);
    if (s === null) return null;
    if (rug) furniture.push({ piece: "int-rug", x: s.x, z: s.z, rot: s.rot, level: L });
    const off = 1.15;
    // Stools face the table: at +x face −x (rot 3), at −x face +x (rot 1),
    // at +z face −z (rot 2), at −z face +z (rot 0) — rot = SIDE_ROT[side].
    const sideOffs: [number, number, 0 | 1 | 2 | 3][] = s.rot % 2 === 0
      ? [[off, 0, 3], [-off, 0, 1]] : [[0, off, 2], [0, -off, 0]];
    for (let i = 0; i < Math.min(seats, 2); i++) {
      const [ox, oz, rot] = sideOffs[i];
      if (!fitsAt("int-stool", s.x + ox, s.z + oz, rot, L)) continue;
      emitPiece("int-stool", s.x + ox, s.z + oz, rot, L, "seat");
    }
    return s;
  };
  const hearthAt = (s: Slot | null, L: number): void => {
    if (s === null) return;
    anchors.push({ kind: "hearth", x: s.x, z: s.z, rot: s.rot, level: L });
    anchors.push({ kind: "lamp", x: s.x, z: s.z, rot: s.rot, level: L });
  };

  // Ground floor per class — pieces land in their OWN rooms (zone preds).
  const inZone = (z: string) => (s: Slot): boolean => zoneOf(s) === z;
  if (cls === "home") {
    // With an upstairs the master bed moves up; the ground bedroom (if carved)
    // still gets one so single-storey homes and big families both read right.
    const bed = putWall("int-bed", 0, inBedroom, "bed")
      ?? (nLevels === 1 ? putWall("int-bed", 0, undefined, "bed") : null);
    if (bed !== null) putWall("int-nightstand", 0, inBedroom);
    putWall("int-cabinet", 0, inBedroom);
    putWall("int-chest", 0, inBedroom);
    // KITCHEN COUNTER (2026-07-31 play report: "counters are still not there",
    // and NPCs leaning where no counter exists). EVERY interior class an NPC
    // can live or work in owns at least one counter — it is the anchor the
    // lean/serve emote pool is gated on (client regionNpcLife.ts), and homes
    // (77% of all enterable buildings in a settlement sweep) had none at all,
    // which is the bulk of what the player was seeing. Ladder: kitchen wall →
    // living wall → any wall; the required-anchor backstop below closes the
    // remaining gap. Placed BEFORE the kitchen table set so the counter wins
    // the galley's one good wall.
    if (putWall("int-counter", 0, inZone("kitchen"), "counter") === null
      && putWall("int-counter", 0, inZone("living"), "counter") === null) {
      putWall("int-counter", 0, undefined, "counter");
    }
    hearthAt(putWall("int-hearth", 0, inZone("living")), 0);
    if (tableSet(0, 2, inZone("kitchen")) !== null) {
      putWall("int-stove", 0, inZone("kitchen"));
      putWall("int-basin", 0, inZone("kitchen"));
      putWall("int-barrel", 0, inZone("kitchen"));
    }
    putWall("int-shelf", 0, inZone("kitchen"));
    tableSet(0, 2, inZone("living"), true);
    putWall("int-bookshelf", 0, inZone("living"));
    putWall("int-plant", 0, inZone("living"));
    putWall("int-lamp", 0, undefined, "lamp");
  } else if (cls === "tavern") {
    // The bar meets the door (store-counter rule); wall bar as fallback.
    if (!putServiceCounter()) putWall("int-counter", 0, inZone("living"), "counter");
    putWall("int-keg", 0, inZone("living"));
    putWall("int-barrel", 0, inZone("living"));
    hearthAt(putWall("int-hearth", 0, inZone("living")), 0);
    for (let i = 0; i < 4; i++) tableSet(0, 2, inZone("living"));
    putWall("int-plant", 0, inZone("living"));
    // Backroom stores the kegs.
    putWall("int-keg", 0, inZone("back"));
    putWall("int-barrel", 0, inZone("back"));
    putWall("grb-crate", 0, inZone("back"));
    putWall("int-lamp", 0, undefined, "lamp");
  } else if (cls === "shop") {
    // Per-USE workplace flavour (P-B4, 2026-07-31): a smithy is not a
    // bookshop — each trade reads through its own tools.
    if (use === USE_SMITHY) {
      putWall("int-forge", 0, undefined, "hearth");
      if (putCentre("int-anvil", 0, undefined, "counter") === null) {
        putWall("int-workbench", 0, undefined, "counter");
      } else putWall("int-workbench", 0);
      putWall("int-barrel", 0);
      if (putWall("grb-crate", 0, inZone("back")) === null) putWall("grb-crate", 0);
    } else if (use === USE_WORKSHOP) {
      putWall("int-workbench", 0, undefined, "counter");
      putWall("int-workbench", 0);
      putCentre("int-table", 0);
      putWall("int-shelf", 0);
      if (putWall("grb-crate", 0, inZone("back")) === null) putWall("grb-crate", 0);
      putWall("int-barrel", 0);
    } else if (use === USE_APOTHECARY) {
      if (!putServiceCounter() && putCentre("int-counter", 0, inZone("living"), "counter") === null) {
        putWall("int-counter", 0, undefined, "counter");
      }
      putCentre("int-cauldron", 0, undefined, "hearth");
      putWall("int-shelf", 0, inZone("living"));
      putWall("int-bookshelf", 0, inZone("living"));
      putWall("int-plant", 0);
      putWall("int-shelf", 0, inZone("back"));
    } else if (use === USE_MARKET_HALL) {
      if (!putServiceCounter() && putCentre("int-counter", 0, inZone("living"), "counter") === null) {
        putWall("int-counter", 0, undefined, "counter");
      }
      putWall("int-lamp", 0, undefined, "lamp"); // reserve the light BEFORE the stalls eat the slots
      putCentre("int-stall", 0);
      putCentre("int-stall", 0);
      putWall("int-shelf", 0, inZone("living"));
      if (putWall("grb-crate", 0, inZone("back")) === null) putWall("grb-crate", 0);
      putWall("int-barrel", 0);
    } else { // depot + fallback: the stockroom read
      if (!putServiceCounter() && putCentre("int-counter", 0, inZone("living"), "counter") === null) {
        putWall("int-counter", 0, undefined, "counter");
      }
      putWall("int-shelf", 0, inZone("living"));
      putWall("int-shelf", 0, inZone("living"));
      if (putWall("grb-crate", 0, inZone("back")) === null) putWall("grb-crate", 0);
      putWall("grb-crate", 0);
      if (putWall("int-barrel", 0, inZone("back")) === null) putWall("int-barrel", 0);
      putWall("int-shelf", 0, inZone("back"));
    }
    putWall("int-lamp", 0, undefined, "lamp");
  } else if (cls === "barracks") {
    // Bunk rows along the walls, a weapon rack as the warden's station,
    // chests at the foot, one mess table.
    putWall("int-rack", 0, undefined, "counter");
    if (putWall("int-bed", 0, undefined, "bed") !== null) putWall("int-chest", 0);
    if (putWall("int-bed", 0, undefined, "bed") !== null) putWall("int-chest", 0);
    putWall("int-bed", 0, undefined, "bed");
    tableSet(0, 2);
    putWall("int-cabinet", 0);
    putWall("int-lamp", 0, undefined, "lamp");
  } else if (cls === "works") {
    // The machine holds the floor; benches line the walls; stock everywhere.
    putWall("int-workbench", 0, undefined, "counter");
    putCentre("int-machine", 0, undefined, "hearth");
    putWall("int-workbench", 0);
    if (putWall("grb-crate", 0, inZone("back")) === null) putWall("grb-crate", 0);
    putWall("grb-crate", 0);
    putWall("int-barrel", 0);
    putWall("int-shelf", 0);
    putWall("int-lamp", 0, undefined, "lamp");
  } else if (cls === "sanctum") {
    // Altar at a wall, bench rows for the faithful, lecterns and light.
    putWall("int-altar", 0, undefined, "counter");
    putWall("int-lamp", 0, undefined, "lamp"); // light reserved before the rows
    const b1 = putCentre("int-bench", 0, undefined, "seat");
    if (b1 !== null) {
      // A second bench row directly behind the first (same facing).
      const bx = b1.rot === 1 ? -1.1 : b1.rot === 3 ? 1.1 : 0;
      const bz = b1.rot === 0 ? -1.1 : b1.rot === 2 ? 1.1 : 0;
      if (fitsAt("int-bench", b1.x + bx, b1.z + bz, b1.rot, 0)) {
        emitPiece("int-bench", b1.x + bx, b1.z + bz, b1.rot, 0, "seat");
      }
    }
    putCentre("int-bench", 0, undefined, "seat");
    putWall("int-lectern", 0);
    putWall("int-plant", 0);
    putWall("int-lamp", 0, undefined, "lamp");
  } else { // civic
    const t1 = putCentre("int-table", 0);
    if (t1 !== null) {
      furniture.push({ piece: "int-rug", x: t1.x, z: t1.z, rot: t1.rot, level: 0 });
      const off = 1.2;
      const benchOffs: [number, number, 0 | 1 | 2 | 3][] = t1.rot % 2 === 0
        ? [[0, off, 2], [0, -off, 0]] : [[off, 0, 3], [-off, 0, 1]];
      for (const [ox, oz, rot] of benchOffs) {
        if (!fitsAt("int-bench", t1.x + ox, t1.z + oz, rot, 0)) continue;
        emitPiece("int-bench", t1.x + ox, t1.z + oz, rot, 0, "seat");
      }
    }
    putWall("int-desk", 0, undefined, "counter");
    putWall("int-lectern", 0, undefined, "counter");
    putWall("int-bookshelf", 0);
    putWall("int-plant", 0);
    putWall("int-lamp", 0, undefined, "lamp");
    putWall("int-lamp", 0, undefined, "lamp");
  }

  // Upper storeys per class (bigger-buildings pass): bedrooms above homes,
  // guest beds above taverns, stock above shops, study above civic halls.
  for (let L = 1; L < nLevels; L++) {
    if (cls === "home") {
      if (putWall("int-bed", L, undefined, "bed") !== null) putWall("int-nightstand", L);
      if ((centreSlotsL[L]?.length ?? 0) >= 4) putWall("int-bed", L, undefined, "bed");
      putWall("int-cabinet", L);
      putWall("int-chest", L);
      const r = (centreSlotsL[L] ?? []).find((s) => !s.taken);
      if (r !== undefined) furniture.push({ piece: "int-rug", x: r.x, z: r.z, rot: r.rot, level: L });
      putWall("int-lamp", L, undefined, "lamp");
    } else if (cls === "tavern") {
      if (putWall("int-bed", L, undefined, "bed") !== null) putWall("int-nightstand", L);
      putWall("int-bed", L, undefined, "bed");
      putWall("int-cabinet", L);
      putWall("int-lamp", L, undefined, "lamp");
    } else if (cls === "shop") {
      putWall("grb-crate", L);
      putWall("int-barrel", L);
      putWall("int-shelf", L);
      putWall("int-lamp", L, undefined, "lamp");
    } else if (cls === "barracks") {
      // More bunks above; a chest per row.
      if (putWall("int-bed", L, undefined, "bed") !== null) putWall("int-chest", L);
      putWall("int-bed", L, undefined, "bed");
      putWall("int-cabinet", L);
      putWall("int-lamp", L, undefined, "lamp");
    } else if (cls === "works") {
      putWall("grb-crate", L);
      putWall("grb-crate", L);
      putWall("int-barrel", L);
      putWall("int-lamp", L, undefined, "lamp");
    } else {
      tableSet(L, 2);
      putWall("int-bookshelf", L);
      putWall("int-shelf", L);
      putWall("int-lamp", L, undefined, "lamp");
    }
  }

  // REQUIRED-ANCHOR BACKSTOP: the class contract (P4's join surface) must
  // survive slot starvation — e.g. a 2-storey home whose upper massing shrank
  // to just the stairwell cell had nowhere for the bed. Any free slot on any
  // level satisfies it; the piece follows the anchor.
  // COUNTER is on EVERY class' list now (2026-07-31): a home's kitchen counter
  // is where its resident cooks, a civic hall's desk is where its clerk stands.
  // Each entry is a SHRINKING piece ladder for one anchor kind: a 2.56 m
  // counter has nowhere to stand in a one-cell hut, but a workbench (1.85 m) or
  // a writing desk (1.55 m) is the same waist-high work surface and does fit —
  // trying the smaller mesh before accepting an overlap cut the measured
  // clipping rate on small footprints roughly in half.
  const COUNTER_PIECES = ["int-counter", "int-workbench", "int-desk"];
  const need: [InteriorAnchor["kind"], string[]][] =
    cls === "home" ? [["bed", ["int-bed"]], ["counter", COUNTER_PIECES], ["lamp", ["int-lamp"]]]
    : cls === "tavern" ? [["counter", COUNTER_PIECES], ["seat", ["int-stool"]], ["lamp", ["int-lamp"]]]
    : cls === "shop" ? [["counter", COUNTER_PIECES], ["lamp", ["int-lamp"]]]
    : cls === "barracks" ? [["bed", ["int-bed"]], ["counter", ["int-rack", "int-chest"]], ["lamp", ["int-lamp"]]]
    : cls === "works" ? [["counter", ["int-workbench", "int-machine"]], ["lamp", ["int-lamp"]]]
    : cls === "sanctum" ? [["counter", ["int-altar", "int-lectern"]], ["seat", ["int-bench", "int-stool"]], ["lamp", ["int-lamp"]]]
    : [["counter", ["int-desk", "int-lectern"]], ["seat", ["int-bench", "int-stool"]], ["lamp", ["int-lamp"]]];
  for (const [kind, pieces] of need) {
    if (anchors.some((a) => a.kind === kind || (kind === "lamp" && a.kind === "hearth"))) continue;
    const piece = pieces[0];
    let done = false;
    for (const cand of pieces) {
      for (let L = 0; L < nLevels && !done; L++) {
        done = putWall(cand, L, undefined, kind) !== null || putCentre(cand, L, undefined, kind) !== null;
      }
      if (done) break;
    }
    // GUARANTEED SPOT (the ladder must not be able to end in nothing): in a
    // one-cell hut three wall slots share one 4 m cell, so a 2.56 m counter and
    // a 2.0 m bed on perpendicular walls cross near the corner and NOTHING
    // clears the strict overlap test. Rather than take the first free slot
    // (measured: that clipped 31% of all plans), search every free slot on
    // every level, every piece in the ladder, and every legal slide along the
    // wall, and take the LEAST-BAD — stopping the instant something fits
    // cleanly. Only if every slot is taken do we stack on level 0's first wall
    // slot. This is the only path that can emit an overlapping piece, so it is
    // also the only thing to look at if clipping is reported again.
    // SLIDE ALONG THE WALL. Slots sit at the MIDPOINT of their cell edge, so in
    // a one-cell room two wall pieces on perpendicular walls always graze at
    // the shared corner even when both would fit if nudged. The backstop may
    // slide a wall piece along its own wall (never off the cell, never off the
    // wall plane) — which turns most of those corner grazes into clean fits.
    const slideSteps = (cand: string, s: Slot): number[] => {
      if (s.side === undefined) return [0];
      const alongX = s.side <= 1;
      const [ew, ed] = extentOf(cand, s.rot);
      const halfAlong = (alongX ? ew : ed) / 2;
      const room = U / 2 - T - halfAlong;
      if (room <= 0.05) return [0];
      return [0, room * 0.5, -room * 0.5, room, -room];
    };
    if (!done) {
      let best: { s: Slot; L: number; p: string; x: number; z: number; over: number } | null = null;
      for (const cand of pieces) {
        for (let L = 0; L < nLevels; L++) {
          for (const s of [...(wallSlotsL[L] ?? []), ...(centreSlotsL[L] ?? [])]) {
            if (s.taken) continue;
            const alongX = s.side !== undefined && s.side <= 1;
            for (const d of slideSteps(cand, s)) {
              const x = s.x + (alongX ? d : 0), z = s.z + (alongX ? 0 : d);
              const over = overlapAt(cand, x, z, s.rot, L);
              if (best === null || over < best.over) best = { s, L, p: cand, x, z, over };
              if (over === 0) break;
            }
            if (best !== null && best.over === 0) break;
          }
          if (best !== null && best.over === 0) break;
        }
        if (best !== null && best.over === 0) break;
      }
      if (best !== null) {
        best.s.taken = true;
        emitPiece(best.p, best.x, best.z, best.s.rot, best.L, kind);
        done = true;
      }
    }
    if (!done) {
      const s = (wallSlotsL[0] ?? [])[0] ?? (centreSlotsL[0] ?? [])[0];
      if (s !== undefined) emitPiece(piece, s.x, s.z, s.rot, 0, kind);
    }
  }

  // WALL ART (2026-07-30 "fun procedural wall art for homes"): 1-2 framed
  // canvases per home level, hung on untaken SOLID wall slots (never across a
  // window or doorway), pushed to the wall plane. No collision (PIECE_BOX has
  // no entry) and the doc carries its own hang height, so this is purely
  // visual; the slot is consumed so a filler can't stand a crate beneath a
  // canvas' wall position.
  if (cls === "home") {
    for (let L = 0; L < nLevels; L++) {
      let want = 1 + (rng() < 0.5 ? 1 : 0);
      for (const slot of wallSlotsL[L] ?? []) {
        if (want === 0) break;
        if (slot.taken || slot.kind !== "solid" || slot.side === undefined) continue;
        const piece = `art-${Math.floor(rng() * 8)}`;
        const ax = slot.x + SIDE_DX[slot.side] * 0.45; // 0.55 slot offset → 0.10 off the wall face
        const az = slot.z + SIDE_DZ[slot.side] * 0.45;
        slot.taken = true;
        emitPiece(piece, ax, az, slot.rot, L);
        want--;
      }
    }
  }

  // Filler densification, ZONE-AWARE (2026-07-30: crates stay out of the
  // bedroom): spare slots get room-fitting flavour pieces so big rooms don't
  // echo. Draw pattern is fixed (one roll per slot) — deterministic; the
  // overlap check inside emit keeps fillers from clipping the class pieces.
  const WALL_FILLERS: Record<string, [string, number][]> = {
    living: [["int-bookshelf", 2], ["int-shelf", 2], ["int-cabinet", 1], ["int-plant", 2], ["int-bench", 1]],
    bedroom: [["int-cabinet", 2], ["int-chest", 2], ["int-nightstand", 2], ["int-plant", 1]],
    kitchen: [["int-shelf", 2], ["int-barrel", 2], ["int-basin", 1], ["grb-crate", 1]],
    back: [["grb-crate", 3], ["int-barrel", 3], ["int-shelf", 1]],
    upper: [["int-cabinet", 2], ["int-chest", 2], ["int-plant", 1], ["int-shelf", 1]],
  };
  const CENTRE_FILLERS: Record<string, [string, number][]> = {
    living: [["table", 2], ["int-rug", 1], ["int-plant", 1]],
    bedroom: [["int-rug", 2], ["int-chest", 1]],
    kitchen: [["int-barrel", 1], ["grb-crate", 1]],
    back: [["grb-crate", 2], ["int-barrel", 2]],
    upper: [["int-rug", 2], ["grb-crate", 1]],
  };
  const weighted = (table: [string, number][], r: number): string => {
    let total = 0; for (const [, w] of table) total += w;
    let acc = r * total;
    for (const [id, w] of table) { acc -= w; if (acc < 0) return id; }
    return table[table.length - 1][0];
  };
  for (let L = 0; L < nLevels; L++) {
    for (const s of wallSlotsL[L]) {
      const roll = rng(), pickRoll = rng(); // fixed draw pattern per slot
      if (s.taken || roll >= 0.45) continue;
      const zn = L === 0 ? zoneOf(s) : "upper";
      const piece = weighted(WALL_FILLERS[zn] ?? WALL_FILLERS.living, pickRoll);
      if (!fitsAt(piece, s.x, s.z, s.rot, L)) continue;
      s.taken = true;
      emitPiece(piece, s.x, s.z, s.rot, L);
    }
    for (const s of centreSlotsL[L]) {
      const roll = rng(), pickRoll = rng();
      if (s.taken || roll >= 0.35) continue;
      const zn = L === 0 ? zoneOf(s) : "upper";
      const pick = weighted(CENTRE_FILLERS[zn] ?? CENTRE_FILLERS.living, pickRoll);
      if (pick === "table") {
        if (!fitsAt("int-table", s.x, s.z, s.rot, L)) continue;
        s.taken = true;
        emitPiece("int-table", s.x, s.z, s.rot, L);
        const off = 1.15;
        const sideOffs: [number, number, 0 | 1 | 2 | 3][] = s.rot % 2 === 0
          ? [[off, 0, 3], [-off, 0, 1]] : [[0, off, 2], [0, -off, 0]];
        for (const [ox, oz, rot] of sideOffs) {
          if (!fitsAt("int-stool", s.x + ox, s.z + oz, rot, L)) continue;
          emitPiece("int-stool", s.x + ox, s.z + oz, rot, L, "seat");
        }
      } else {
        if (pick !== "int-rug" && !fitsAt(pick, s.x, s.z, s.rot, L)) continue;
        s.taken = true;
        emitPiece(pick, s.x, s.z, s.rot, L);
      }
    }
  }

  // ── collision: partitions (walls) + furniture boxes ────────────────────────
  const collision: LocalBox[] = [];
  const H = LEVEL_H_M;
  for (const w of partitions) {
    const ex = w.x + SIDE_DX[w.side] * (U / 2 - T / 2);
    const ez = w.z + SIDE_DZ[w.side] * (U / 2 - T / 2);
    const alongX = w.side <= 1;
    if (w.kind !== "door") {
      collision.push({
        x: ex, y: 0, z: ez,
        w: alongX ? U : T, h: H, d: alongX ? T : U, wall: true,
      });
      continue;
    }
    const flank = (U - SHELL_DOOR_W) / 2;
    const off = SHELL_DOOR_W / 2 + flank / 2;
    if (alongX) {
      collision.push({ x: ex - off, y: 0, z: ez, w: flank, h: H, d: T, wall: true });
      collision.push({ x: ex + off, y: 0, z: ez, w: flank, h: H, d: T, wall: true });
      collision.push({ x: ex, y: SHELL_DOOR_HEAD_M, z: ez, w: SHELL_DOOR_W, h: H - SHELL_DOOR_HEAD_M, d: T, wall: true });
    } else {
      collision.push({ x: ex, y: 0, z: ez - off, w: T, h: H, d: flank, wall: true });
      collision.push({ x: ex, y: 0, z: ez + off, w: T, h: H, d: flank, wall: true });
      collision.push({ x: ex, y: SHELL_DOOR_HEAD_M, z: ez, w: T, h: H - SHELL_DOOR_HEAD_M, d: SHELL_DOOR_W, wall: true });
    }
  }
  for (const f of furniture) {
    const ext = PIECE_BOX.get(f.piece);
    if (ext === null || ext === undefined) continue;
    const [fw, fh, fd] = ext;
    const w = f.rot % 2 === 0 ? fw : fd;
    const d = f.rot % 2 === 0 ? fd : fw;
    collision.push({
      x: f.x, y: f.level === 0 ? sh.floorTopM : f.level * H, z: f.z, w, h: fh, d,
    });
  }

  return { cls, rooms, partitions, furniture, anchors, collision, floorTopM: sh.floorTopM };
}
